import { addDays, fromIso, isSameDay, startOfDay } from "../time.js";
import type { Priority, Status, Task } from "./types.js";

export interface TaskFilter {
  status?: Status | Status[];
  priority?: Priority | Priority[];
  includeDeleted?: boolean;
}

/** In-memory filter helper. SQL filtering lives in packages/storage. */
export function filterTasks(tasks: Task[], f: TaskFilter = {}): Task[] {
  const statuses = f.status ? ([] as Status[]).concat(f.status) : null;
  const prios = f.priority ? ([] as Priority[]).concat(f.priority) : null;
  return tasks.filter((t) => {
    if (!f.includeDeleted && t.deleted_at) return false;
    if (statuses && !statuses.includes(t.status)) return false;
    if (prios && !prios.includes(t.priority)) return false;
    return true;
  });
}

/** Tasks with a due date on the given day (local time). Multi-day all-day tasks appear on every day they span. */
export function tasksOnDay(tasks: Task[], day: Date): Task[] {
  const dayStart = startOfDay(day);
  return tasks.filter((t) => {
    if (!t.due_at) return false;
    const taskStart = fromIso(t.due_at);
    if (t.all_day && t.duration_minutes > 1440) {
      const spanDays = Math.ceil(t.duration_minutes / 1440);
      const taskEnd = addDays(startOfDay(taskStart), spanDays - 1);
      return dayStart >= startOfDay(taskStart) && dayStart <= taskEnd;
    }
    return isSameDay(taskStart, day);
  });
}

const PRIO_ORDER: Record<Priority, number> = { high: 0, medium: 1, low: 2, none: 3 };

/** Sort by due date ascending (nulls last), then priority high→none. */
export function sortByDue(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.due_at && b.due_at) {
      const cmp = a.due_at.localeCompare(b.due_at);
      if (cmp !== 0) return cmp;
    } else if (a.due_at) {
      return -1;
    } else if (b.due_at) {
      return 1;
    }
    return PRIO_ORDER[a.priority] - PRIO_ORDER[b.priority];
  });
}
